import React from 'react';
import styled from 'styled-components';

// Import all your local Tourmaline images
import paraibaTourmaline from '../../assets/image/Paraiba Tourmaline.jpeg';
import rubelliteTourmaline from '../../assets/image/Rubellite Tourmaline.jpeg';
import chromeTourmaline from '../../assets/image/Chrome Tourmaline.jpeg';
import indicolite from '../../assets/image/Indicolite.jpeg';
import watermelonTourmaline from '../../assets/image/Watermelon Tourmaline.jpeg';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeroSection = styled.section`
  height: 60vh;
  min-height: 500px;
  background: linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)),
              url('https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-bottom: 60px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.3);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  margin-bottom: 20px;
  letter-spacing: 3px;
  font-weight: 300;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  span {
    color: #c4a484;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
  opacity: 0.95;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  text-align: center;
  margin: 60px 0 40px;
  color: #1a2a3a;
  font-weight: 300;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 2px;
    background: #c4a484;
    margin: 20px auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const GemCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
`;

const GemImageWrapper = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to top, rgba(0,0,0,0.3), transparent);
    pointer-events: none;
  }
`;

const GemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  background-color: #f8f9fa;
  transition: transform 0.5s ease;
  
  ${GemCard}:hover & {
    transform: scale(1.05);
  }
`;

const GemInfo = styled.div`
  padding: 25px;
`;

const GemName = styled.h3`
  font-size: 24px;
  color: #1a2a3a;
  margin-bottom: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const GemDetails = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
  font-size: 14px;
`;

const GemProperties = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
`;

const Property = styled.span`
  background: #f8f9fa;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  color: #555;
  border: 1px solid #e0e0e0;
  display: inline-flex;
  align-items: center;
  
  &:before {
    content: '✦';
    color: #c4a484;
    margin-right: 5px;
    font-size: 10px;
  }
`;

const Tag = styled.span`
  background: ${props => {
    if (props.tag === 'HOT') return '#ff4757';
    if (props.tag === 'NEW') return '#2ecc71';
    if (props.tag === 'RARE') return '#9b59b6';
    if (props.tag === 'EXCEPTIONAL') return '#f39c12';
    return '#c4a484';
  }};
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  white-space: nowrap;
`;

const OriginBadge = styled.span`
  background: #1a2a3a;
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  margin-top: 10px;
`;

const ColorBar = styled.div`
  height: 6px;
  width: 100%;
  background: ${props => props.color};
  border-radius: 3px;
  margin: 15px 0 5px;
`;

const Tourmaline = () => {
  // Tourmaline gems for the grid
  const gems = [
    {
      name: "Paraiba Tourmaline",
      image: paraibaTourmaline,
      description: "The most valuable tourmaline, with electric blue-green color from copper. Rarer than diamonds, these gems from Paraiba, Brazil, are prized for their neon glow.",
      properties: ["Hardness: 7-7.5", "Color: Neon Blue", "Origin: Brazil"],
      origin: "Paraiba, Brazil",
      tag: "EXCEPTIONAL",
      price: "$15,000 - $50,000+ per carat",
      colorBar: "linear-gradient(135deg, #00ffff, #40e0d0)"
    },
    {
      name: "Rubellite Tourmaline",
      image: rubelliteTourmaline,
      description: "Pink to red tourmaline that maintains its color in all lighting conditions. The name comes from 'rubellus' meaning reddish, and fine specimens rival ruby in beauty.",
      properties: ["Hardness: 7-7.5", "Color: Pink-Red", "Stable Color"],
      origin: "Afghanistan/Nigeria",
      tag: "RARE",
      price: "$3,000 - $15,000 per carat",
      colorBar: "linear-gradient(135deg, #ff69b4, #ff1493)"
    },
    {
      name: "Chrome Tourmaline",
      image: chromeTourmaline,
      description: "Intense green tourmaline colored by chromium and vanadium, rivaling emerald in beauty. Found mainly in Tanzania, it displays a rich, deep green hue.",
      properties: ["Hardness: 7-7.5", "Color: Emerald Green", "Chromium Rich"],
      origin: "Tanzania",
      tag: "",
      price: "$2,000 - $8,000 per carat",
      colorBar: "linear-gradient(135deg, #2e8b57, #228b22)"
    },
    {
      name: "Indicolite",
      image: indicolite,
      description: "Blue tourmaline ranging from light sky blue to deep indigo. The name comes from 'indigo,' and fine specimens display a rich, saturated blue color.",
      properties: ["Hardness: 7-7.5", "Color: Blue to Indigo", "Pleochroic"],
      origin: "Brazil/Mozambique",
      tag: "HOT",
      price: "$2,500 - $10,000 per carat",
      colorBar: "linear-gradient(135deg, #4169e1, #0000cd)"
    },
    {
      name: "Watermelon Tourmaline",
      image: watermelonTourmaline,
      description: "Unique tourmaline with pink center and green outer layer, resembling a watermelon slice when cut. This color zoning is caused by changing conditions during formation.",
      properties: ["Hardness: 7-7.5", "Color: Pink/Green", "Bi-color"],
      origin: "Brazil",
      tag: "",
      price: "$1,500 - $6,000 per carat",
      colorBar: "linear-gradient(135deg, #ff69b4, #228b22)"
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <span>Tourmaline</span> Collection
          </HeroTitle>
          <HeroSubtitle>
            The rainbow gemstone • More colors than any other gem family • Paraiba • Rubellite • Indicolite
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      
      <SectionTitle>The Tourmaline Spectrum</SectionTitle>
      <Grid>
        {gems.map((gem, index) => (
          <GemCard key={index}>
            <GemImageWrapper>
              <GemImage src={gem.image} alt={gem.name} />
            </GemImageWrapper>
            <GemInfo>
              <GemName>
                {gem.name}
                {gem.tag && <Tag tag={gem.tag}>{gem.tag}</Tag>}
              </GemName>
              <GemDetails>{gem.description}</GemDetails>
              <OriginBadge>📍 {gem.origin}</OriginBadge>
              <ColorBar color={gem.colorBar} />
              <GemProperties>
                {gem.properties.map((prop, i) => (
                  <Property key={i}>{prop}</Property>
                ))}
              </GemProperties>
              <GemDetails style={{ marginTop: '10px', color: '#c4a484', fontWeight: '500' }}>
                {gem.price}
              </GemDetails>
            </GemInfo>
          </GemCard>
        ))}
      </Grid>
    </PageContainer>
  );
};

export default Tourmaline;